import { useEffect, useRef } from 'react';
import type { VoidFunctionComponent } from 'react';
import Button from '../../../generic/Button';
import { FormGroup, LabeledTextInput, LabeledPasswordInput } from '../../../generic/Forms';
import type { NugetSource } from '../../../../../typings/auth';

function isV3Url(url?: string) {
  return url?.endsWith('/v3/index.json') ?? false;
}

type SourceProps = {
  source: NugetSource;
  onUpdateSource: <K extends keyof NugetSource>(member: K, value: NugetSource[K]) => void;
  onRemoveSource: () => void;
};

const Source: VoidFunctionComponent<SourceProps> = ({
  source,
  onUpdateSource,
  onRemoveSource,
}) => {
  const isV3 = isV3Url(source.url);
  const wasV3 = useRef<boolean>();

  useEffect(() => {
    // When switching to a v3 source set 'x' as the password (v3 works with any password).
    // When switching to a v2 source clear the 'x' password.
    if (wasV3.current === false && isV3) { // explicitly check for false because could be undefined
      onUpdateSource('password', { __type: 's', value: 'x' });
    } else if (wasV3.current && !isV3) {
      onUpdateSource('password', { __type: 's', value: '' });
    }
    wasV3.current = isV3;
  });

  return (
    <div className="margin-bottom-sm">
      <FormGroup direction="row" spacing="sm" baselineAlignment>
        <LabeledTextInput
          label="URL"
          size="small"
          value={source.url || ''}
          onChange={value => onUpdateSource('url', value)}
          placeholder="https://github.com/org/repo"
        />
        <LabeledTextInput
          label="Username"
          size="small"
          value={source.username || ''}
          onChange={value => onUpdateSource('username', value)}
          placeholder="someuser"
        />
        {!isV3 && (
          <LabeledPasswordInput
            label="Password"
            size="small"
            value={source.hasPassword ? '••••••••' : source.password?.value || ''}
            onChange={value => onUpdateSource('password', { __type: 's', value })}
          />
        )}
        <Button
          type="error"
          size="small"
          onClick={onRemoveSource}
        >
          Remove
        </Button>
      </FormGroup>
    </div>
  );
};

export default Source;
